import { ArrowLeft } from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

/**
 * A way out of a screen the rail does not carry (D-353).
 *
 * **Four routes had none at all.** `/conflicts`, `/admin/booking-approvals`,
 * `/account` and `/find` are in `App.tsx` but in neither `ALL_DESTINATIONS` nor
 * `VIEWS`, so no rail item, no view bar and no back link — reached from a home
 * alert, an avatar menu or a server-built search href, and then a dead end. The
 * owner asked for *"a back button when on any page"*; this answers the narrower
 * question, which is the four journeys that actually strand somebody.
 *
 * **It is not a fourth navigation.** D-322 put every destination in the rail and
 * D-331 removed the last global control that duplicated what the chrome already
 * said. A row on every screen would repeat the rail nine times over to serve
 * four screens that are missing from it.
 *
 * **History when there is history, a named place when there is not.** Both
 * halves were rejected on their own and each fails the other's case:
 *
 * - A fixed target cannot serve a screen reached from several doors. The dancer
 *   record said *"All dancers"* however you arrived, so opening it from a search
 *   hit or a `Dancer` notification named a list she had never seen. *A link that
 *   refuses the person holding it is worse than no link*, and one naming
 *   somewhere she has never been is the same fault a step milder.
 * - History has no answer for a pasted URL or a cold deep link, and a control
 *   that silently does nothing is worse than no control.
 *
 * **The test for "is there anything behind her" is the router's own index, and
 * the obvious alternative is wrong in the browser and right in the tests.**
 * `location.key !== 'default'` looks like it means she navigated here, and does
 * not: `RequireSession` and `SignInView` both redirect with `replace`, which
 * mints a fresh key while leaving the index where it was. So the most ordinary
 * cold arrival there is — a link in an email, opened while signed out, bounced
 * through sign-in and back — produced a *Back* button on history entry zero,
 * and tapping it left the application for whatever site referred her. **A
 * `MemoryRouter` cannot reproduce that**, because memory history clamps `go()`
 * to its own bounds while `getUrlBasedHistory` passes the delta straight to
 * `window.history`. Green tests, broken button.
 *
 * `idx` is stamped on every entry the router creates, is `0` on the one the app
 * opened with, and only grows on a push. Under a `MemoryRouter` it is absent,
 * which reads as *no history* — the safe direction, and the reason the tests
 * that exercise this drive a `BrowserRouter`.
 *
 * **Going back is then almost always safe**, because she was there a moment ago
 * — which is what keeps this from becoming a second place deciding *may she go
 * here*, the drift D-322 warns about. *Almost*: a capability revoked in between
 * calls `InvalidateSessions()` (D-72, D-73), so the previous screen can refuse
 * her by the time she returns. The server re-checks, so that is a refusal
 * rather than a leak, and no client-side control can do better without becoming
 * the second answer to a question the navigation already answers.
 *
 * **The fallback is still a real decision** — it runs when she has no history,
 * so it must be a place this reader can actually reach. Prefer a destination
 * over a screen with its own gate.
 */
/** Whether the router has an entry of its own behind this one. */
function hasHistoryBehind(): boolean {
  const idx = (window.history.state as { idx?: number } | null)?.idx

  // Absent under a MemoryRouter, and absent reads as "nothing behind her" —
  // which renders the fallback, the branch that can never leave the app.
  return typeof idx === 'number' && idx > 0
}

export default function BackLink({ to, label }: { to: string; label: string }) {
  const navigate = useNavigate()

  /*
   * Defensive, and deliberately labelled as such rather than given a reason it
   * does not have. Every screen carrying this one unmounts on navigation, and
   * the only one that changes its own query does so with `replace`, so nothing
   * today can move `idx` underneath a mounted instance — deleting this line
   * breaks no test. It stays because the first screen that keeps a `BackLink`
   * mounted across a push would otherwise hold whichever branch it first drew.
   */
  useLocation()

  // `min-h`/`px` rather than the bare inline-flex it inherited: the control was
  // 47x19 and the layout audit refuses anything under 24px (D-255).
  const className =
    'mb-4 -ml-1.5 inline-flex min-h-[28px] items-center gap-1.5 rounded-lg px-1.5 text-[12.5px] font-semibold text-ink-3 no-underline hover:text-ink-2'

  if (!hasHistoryBehind()) {
    return (
      <Link to={to} className={className}>
        <ArrowLeft size={14} strokeWidth={2.5} />
        {label}
      </Link>
    )
  }

  /*
   * A button rather than a link, because there is no href to give it — and
   * saying "Back" rather than naming a place is the honest label when the place
   * is wherever she happens to have come from.
   */
  return (
    <button type="button" onClick={() => navigate(-1)} className={className}>
      <ArrowLeft size={14} strokeWidth={2.5} />
      Back
    </button>
  )
}
